import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { 
  Activity, Play, RefreshCw, XCircle, Search, TrendingUp,
  BarChart3, Clock, CheckCircle, AlertTriangle, Settings,
  Calendar, Database, Zap, Timer, HelpCircle, Network,
  Target, RotateCcw, Trash2, Eye
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Metric definitions for tooltips
const METRIC_DEFINITIONS = {
  global_onion_layer: "The onion layer in the global co-holding graph. Higher values indicate stocks that are more 'central' - held together by many elite funds.",
  global_centrality_norm: "Normalized centrality score (0-1). Higher = more central in the network of elite fund holdings.",
  sector_onion_layer: "The onion layer within the sector-specific co-holding graph. Higher = more central within the sector.",
  sector_centrality_norm: "Normalized sector centrality (0-1). Measures how central this stock is among top funds in its sector.",
  component_size: "Number of stocks in the connected component. Larger components indicate stocks are part of a well-connected holding network.",
};

const CentralityPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [config, setConfig] = useState({});
  const [configDirty, setConfigDirty] = useState(false);
  
  // Data state
  const [centralityData, setCentralityData] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [searchSymbol, setSearchSymbol] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  
  // Observation dates
  const [availableDates, setAvailableDates] = useState([]);
  const [processedDates, setProcessedDates] = useState([]);
  const [missingDates, setMissingDates] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [obsDateStatus, setObsDateStatus] = useState([]);
  const [schedule, setSchedule] = useState(null);
  
  // Run history
  const [runHistory, setRunHistory] = useState([]);
  const [runLogs, setRunLogs] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  
  // Top central stocks
  const [topCentral, setTopCentral] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API}/centrality/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setStats(result);
        setRunning(result.state?.status === "running");
        if (result.config) {
          setConfig(result.config);
        }
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchAvailableDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/centrality/obs-dates/available`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setAvailableDates(result.available || []);
        setProcessedDates(result.processed || []);
        setMissingDates(result.missing || []);
        if (result.processed?.length > 0 && !selectedDate) {
          setSelectedDate(result.processed[result.processed.length - 1]);
        } else if (result.available?.length > 0 && !selectedDate) {
          setSelectedDate(result.available[result.available.length - 1]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch available dates:", error);
    }
  }, [token, selectedDate]);

  const fetchObsDateStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/centrality/obs-dates/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setObsDateStatus(result.obs_dates || []);
      }
    } catch (error) {
      console.error("Failed to fetch obs date status:", error);
    }
  }, [token]);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await fetch(`${API}/centrality/schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setSchedule(result);
      }
    } catch (error) {
      console.error("Failed to fetch schedule:", error);
    }
  }, [token]);

  const fetchRunHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API}/centrality/runs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunHistory(result.runs || []);
        // Set selected run to latest if not set
        if (!selectedRunId && result.runs?.length > 0) {
          setSelectedRunId(result.runs[0].run_id);
        }
      }
    } catch (error) {
      console.error("Failed to fetch run history:", error);
    }
  }, [token, selectedRunId]);

  const fetchRunLogs = useCallback(async () => {
    if (!selectedRunId) return;
    
    try {
      const response = await fetch(`${API}/centrality/runs/${selectedRunId}/logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setRunLogs(result.logs || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs:", error);
    }
  }, [token, selectedRunId]);

  const fetchCentralityData = useCallback(async () => {
    if (!selectedDate) return;
    
    setDataLoading(true);
    try {
      let url = `${API}/centrality/data?limit=100`;
      if (selectedDate) {
        url += `&observation_date=${selectedDate}`;
      }
      if (searchSymbol) {
        url += `&symbol=${searchSymbol.toUpperCase()}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setCentralityData(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch centrality data:", error);
    } finally {
      setDataLoading(false);
    }
  }, [token, selectedDate, searchSymbol]);

  const fetchTopCentral = useCallback(async () => {
    if (!selectedDate) return;
    
    try {
      const response = await fetch(`${API}/centrality/data/top-central?observation_date=${selectedDate}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setTopCentral(result.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch top central:", error);
    }
  }, [token, selectedDate]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchAvailableDates();
    fetchRunHistory();
    fetchSchedule();
  }, []);

  // Polling when running
  useEffect(() => {
    if (running) {
      const interval = setInterval(() => {
        fetchStats();
        if (activeTab === "logs") {
          fetchRunLogs();
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [running, activeTab, fetchStats, fetchRunLogs]);

  // Load data when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchCentralityData();
      fetchTopCentral();
    }
  }, [selectedDate, fetchCentralityData, fetchTopCentral]);

  // Load logs when run selected
  useEffect(() => {
    if (selectedRunId) {
      fetchRunLogs();
    }
  }, [selectedRunId, fetchRunLogs]);

  // Handlers
  const handleRun = async (runType) => {
    try {
      const response = await fetch(`${API}/centrality/run/${runType}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`${runType} centrality pipeline started`);
        setRunning(true);
        fetchStats();
        fetchRunHistory();
        setActiveTab("logs");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start pipeline");
      }
    } catch (error) {
      toast.error("Failed to start pipeline");
    }
  };

  const handleRunSelectedDates = async () => {
    if (selectedDates.length === 0) {
      toast.error("Please select at least one observation date");
      return;
    }
    
    try {
      const response = await fetch(`${API}/centrality/run/dates`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(selectedDates)
      });
      if (response.ok) {
        toast.success(`Started processing ${selectedDates.length} observation date(s)`);
        setRunning(true);
        setSelectedDates([]);
        setActiveTab("logs");
        fetchStats();
        fetchRunHistory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start");
      }
    } catch (error) {
      toast.error("Failed to start processing");
    }
  };

  const handleCancel = async () => {
    try {
      const response = await fetch(`${API}/centrality/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Cancel signal sent");
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to cancel");
    }
  };

  const handleForceReset = async () => {
    try {
      const response = await fetch(`${API}/centrality/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success("Pipeline reset to idle");
        setRunning(false);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to reset");
    }
  };

  const handleDeleteObsDate = async (obsDate) => {
    if (!window.confirm(`Delete all centrality data for ${obsDate}?`)) return;
    
    try {
      const response = await fetch(`${API}/centrality/obs-dates/${obsDate}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`Deleted data for ${obsDate}`);
        fetchAvailableDates();
        fetchObsDateStatus();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleReprocessObsDate = async (obsDate) => {
    try {
      const response = await fetch(`${API}/centrality/obs-dates/${obsDate}/reprocess`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success(`Started reprocessing ${obsDate}`);
        setRunning(true);
        fetchStats();
        setActiveTab("logs");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to start reprocessing");
      }
    } catch (error) {
      toast.error("Failed to start reprocessing");
    }
  };

  const handleSaveConfig = async () => {
    try {
      const response = await fetch(`${API}/centrality/config`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(config),
      });
      if (response.ok) {
        toast.success("Configuration saved");
        setConfigDirty(false);
        fetchStats();
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const toggleDateSelection = (date) => {
    setSelectedDates(prev => 
      prev.includes(date) 
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Network className="w-6 h-6 text-primary" />
              Stock Centrality Engine
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Graph-based centrality analysis of elite fund co-holdings using onion-layer decomposition
            </p>
          </div>
          <div className="flex gap-2">
            {running ? (
              <>
                <Button variant="destructive" size="sm" onClick={handleCancel}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleRun("incremental")}
                  data-testid="run-incremental-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Run Incremental
                </Button>
                <Button 
                  size="sm" 
                  onClick={() => handleRun("full")}
                  data-testid="run-full-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Run Full
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={handleForceReset}>
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={fetchStats}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        {running && stats?.state?.progress && (
          <Card className="border-primary/50 bg-primary/5">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                <div className="flex-1">
                  <p className="font-medium">{stats.state.progress.message || "Processing..."}</p>
                  {stats.state.progress.dates_total > 0 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-sm text-muted-foreground mb-1">
                        <span>Progress: {stats.state.progress.dates_done || 0} / {stats.state.progress.dates_total} dates</span>
                        <span>{Math.round(((stats.state.progress.dates_done || 0) / stats.state.progress.dates_total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all"
                          style={{ width: `${((stats.state.progress.dates_done || 0) / stats.state.progress.dates_total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Records</p>
                  <p className="text-2xl font-bold">{stats?.total_rows?.toLocaleString() || 0}</p>
                </div>
                <Database className="w-8 h-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Observation Dates</p>
                  <p className="text-2xl font-bold">{stats?.unique_dates || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Unique Stocks</p>
                  <p className="text-2xl font-bold">{stats?.unique_symbols?.toLocaleString() || 0}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge 
                    variant={running ? "default" : "secondary"}
                    className={running ? "bg-green-500" : ""}
                  >
                    {stats?.state?.status || "idle"}
                  </Badge>
                </div>
                <Activity className="w-8 h-8 text-muted-foreground/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="data">Centrality Data</TabsTrigger>
            <TabsTrigger value="obs-dates">Observation Dates</TabsTrigger>
            <TabsTrigger value="config">Configuration</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
            <TabsTrigger value="history">Run History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {/* Quarterly Computation Schedule */}
            {schedule && (
              <Card data-testid="centrality-schedule-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Quarterly Computation Schedule
                      </CardTitle>
                      <CardDescription>
                        Aligned with 13F filing deadlines (+{schedule.obs_lag_days}d lag, snapped to trading day)
                      </CardDescription>
                    </div>
                    {schedule.next_computation_date && (
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Next computation</p>
                        <p className="text-lg font-bold text-primary" data-testid="next-computation-date">
                          {schedule.next_computation_date}
                        </p>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {(schedule.schedule || [])
                      .filter(s => {
                        const d = new Date(s.computation_date);
                        const now = new Date();
                        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 8, 1);
                        return d >= sixMonthsAgo;
                      })
                      .map(s => (
                        <div
                          key={s.computation_date}
                          className={`px-3 py-2 rounded-lg border text-sm ${
                            s.status === "computed"
                              ? "bg-green-500/10 border-green-500/30"
                              : s.status === "pending"
                              ? "bg-amber-500/10 border-amber-500/40"
                              : "bg-muted/40 border-border"
                          }`}
                          data-testid={`schedule-date-${s.computation_date}`}
                        >
                          <div className="font-mono font-medium">{s.computation_date}</div>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-xs text-muted-foreground">{s.quarter} filings</span>
                            <Badge
                              variant={s.status === "computed" ? "default" : s.status === "pending" ? "destructive" : "outline"}
                              className="text-[10px] px-1.5 py-0"
                            >
                              {s.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                  </div>
                  {schedule.pending_dates?.length > 0 && (
                    <p className="text-xs text-amber-500 mt-3">
                      ⚠ {schedule.pending_dates.length} date(s) overdue: {schedule.pending_dates.join(", ")} — run the pipeline to catch up
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Top Central Stocks */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Most Central Stocks
                  </CardTitle>
                  <CardDescription>
                    Stocks with highest onion layer (most central in elite fund holdings)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-3">
                    <Label>Select Observation Date</Label>
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="w-full mt-1 p-2 rounded border bg-background"
                    >
                      {processedDates.map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </select>
                  </div>
                  <ScrollArea className="h-[300px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">#</th>
                          <th className="text-left py-2 px-2">Symbol</th>
                          <th className="text-left py-2 px-2">Cap</th>
                          <th className="text-right py-2 px-2">
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 justify-end">
                                G.Layer <HelpCircle className="w-3 h-3" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {METRIC_DEFINITIONS.global_onion_layer}
                              </TooltipContent>
                            </Tooltip>
                          </th>
                          <th className="text-right py-2 px-2">
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 justify-end">
                                G.Norm <HelpCircle className="w-3 h-3" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {METRIC_DEFINITIONS.global_centrality_norm}
                              </TooltipContent>
                            </Tooltip>
                          </th>
                          <th className="text-right py-2 px-2">S.Layer</th>
                          <th className="text-right py-2 px-2">S.Norm</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCentral.map((row, i) => (
                          <tr key={`${row.symbol}-${row.cap_bucket}`} className="border-b border-muted/50 hover:bg-muted/20">
                            <td className="py-2 px-2 text-muted-foreground">{i + 1}</td>
                            <td className="py-2 px-2 font-mono font-medium">{row.symbol}</td>
                            <td className="py-2 px-2 text-xs">{row.cap_bucket ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.global_onion_layer}</td>
                            <td className="py-2 px-2 text-right">{row.global_centrality_norm?.toFixed(3)}</td>
                            <td className="py-2 px-2 text-right">{row.sector_onion_layer ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.sector_centrality_norm?.toFixed(3) ?? '-'}</td>
                          </tr>
                        ))}
                        {topCentral.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-4 text-center text-muted-foreground">
                              No data available. Run the centrality pipeline first.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Pipeline Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Pipeline Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Date Range</Label>
                      <p className="font-medium">
                        {stats?.date_range?.oldest ? (
                          `${stats.date_range.oldest.split('T')[0]} to ${stats.date_range.newest?.split('T')[0]}`
                        ) : 'No data'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Last Run</Label>
                      <p className="font-medium">
                        {stats?.state?.last_run_at ? new Date(stats.state.last_run_at).toLocaleString() : 'Never'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Available Skill Dates</Label>
                      <p className="font-medium">{availableDates.length}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Missing Dates</Label>
                      <p className="font-medium text-amber-500">{missingDates.length}</p>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Label className="text-muted-foreground mb-2 block">Current Configuration</Label>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span>Top Global %:</span>
                        <span className="font-mono">{config.top_global_percentile || 5}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Top Sector %:</span>
                        <span className="font-mono">{config.top_sector_percentile || 5}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Onion Alpha:</span>
                        <span className="font-mono">{config.onion_alpha || 1.0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Onion Beta:</span>
                        <span className="font-mono">{config.onion_beta || 1.0}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Data Tab */}
          <TabsContent value="data" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Centrality Data Explorer</CardTitle>
                  <div className="flex gap-2">
                    <select
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="p-2 rounded border bg-background text-sm"
                    >
                      <option value="">All Dates</option>
                      {processedDates.map(date => (
                        <option key={date} value={date}>{date}</option>
                      ))}
                    </select>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search symbol..."
                        value={searchSymbol}
                        onChange={(e) => setSearchSymbol(e.target.value)}
                        className="pl-8 w-40"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchCentralityData}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {dataLoading ? (
                  <div className="flex justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-background">
                        <tr className="border-b">
                          <th className="text-left py-2 px-2">Symbol</th>
                          <th className="text-left py-2 px-2">Date</th>
                          <th className="text-left py-2 px-2">Cap</th>
                          <th className="text-left py-2 px-2">Sector</th>
                          <th className="text-right py-2 px-2">G.Layer</th>
                          <th className="text-right py-2 px-2">G.Norm</th>
                          <th className="text-right py-2 px-2">S.Layer</th>
                          <th className="text-right py-2 px-2">S.Norm</th>
                        </tr>
                      </thead>
                      <tbody>
                        {centralityData.map((row, i) => (
                          <tr key={`${row.symbol}-${row.observation_date}-${row.cap_bucket}-${i}`} className="border-b border-muted/50 hover:bg-muted/20">
                            <td className="py-2 px-2 font-mono font-medium">{row.symbol}</td>
                            <td className="py-2 px-2">{row.observation_date}</td>
                            <td className="py-2 px-2">{row.cap_bucket ?? '-'}</td>
                            <td className="py-2 px-2">{row.sector ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.global_onion_layer ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.global_centrality_norm?.toFixed(3) ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.sector_onion_layer ?? '-'}</td>
                            <td className="py-2 px-2 text-right">{row.sector_centrality_norm?.toFixed(3) ?? '-'}</td>
                          </tr>
                        ))}
                        {centralityData.length === 0 && (
                          <tr>
                            <td colSpan={8} className="py-8 text-center text-muted-foreground">
                              No centrality data found. Run the pipeline or adjust filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Observation Dates Tab */}
          <TabsContent value="obs-dates" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Available Dates Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Available Observation Dates</CardTitle>
                  <CardDescription>
                    Select dates to process (from fund_sector_skill_12m)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[300px]">
                    <div className="space-y-1">
                      {availableDates.map(date => {
                        const isProcessed = processedDates.includes(date);
                        const isSelected = selectedDates.includes(date);
                        return (
                          <div 
                            key={date}
                            className={`flex items-center justify-between p-2 rounded cursor-pointer ${
                              isSelected ? 'bg-primary/20 border border-primary/50' : 'hover:bg-muted/50'
                            }`}
                            onClick={() => toggleDateSelection(date)}
                          >
                            <div className="flex items-center gap-2">
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={() => {}}
                                className="w-4 h-4"
                              />
                              <span className="font-mono">{date}</span>
                            </div>
                            <Badge variant={isProcessed ? "default" : "outline"}>
                              {isProcessed ? "Processed" : "Pending"}
                            </Badge>
                          </div>
                        );
                      })}
                      {availableDates.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          No skill data available. Run the Fund Intelligence pipeline first.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                  <div className="mt-4 flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">
                      {selectedDates.length} date(s) selected
                    </span>
                    <Button 
                      onClick={handleRunSelectedDates}
                      disabled={selectedDates.length === 0 || running}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Process Selected
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Processed Dates Management */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Processed Dates</CardTitle>
                  <CardDescription>
                    Manage existing centrality computations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[350px]">
                    <div className="space-y-1">
                      {processedDates.map(date => {
                        const status = obsDateStatus.find(s => s.observation_date === date);
                        return (
                          <div 
                            key={date}
                            className="flex items-center justify-between p-2 rounded border border-muted/50"
                          >
                            <div>
                              <span className="font-mono">{date}</span>
                              {status?.rows && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  ({status.rows.toLocaleString()} records)
                                </span>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  setSelectedDate(date);
                                  setActiveTab("data");
                                }}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleReprocessObsDate(date)}
                                disabled={running}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteObsDate(date)}
                                disabled={running}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {processedDates.length === 0 && (
                        <p className="text-center text-muted-foreground py-4">
                          No dates processed yet.
                        </p>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Configuration Tab */}
          <TabsContent value="config" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Centrality Configuration</CardTitle>
                <CardDescription>
                  Configure the onion-layer centrality computation parameters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Top Fund Selection */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Top Fund Selection</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Top Global Percentile: {config.top_global_percentile || 5}%</Label>
                        <Slider
                          value={[config.top_global_percentile || 5]}
                          onValueChange={([v]) => {
                            setConfig({ ...config, top_global_percentile: v });
                            setConfigDirty(true);
                          }}
                          min={1}
                          max={20}
                          step={1}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Top X% of funds by global skill score
                        </p>
                      </div>
                      <div>
                        <Label>Top Sector Percentile: {config.top_sector_percentile || 5}%</Label>
                        <Slider
                          value={[config.top_sector_percentile || 5]}
                          onValueChange={([v]) => {
                            setConfig({ ...config, top_sector_percentile: v });
                            setConfigDirty(true);
                          }}
                          min={1}
                          max={20}
                          step={1}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Top X% of funds by sector skill score
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Onion Parameters */}
                  <div className="space-y-4">
                    <h3 className="font-medium">Onion Centrality Parameters</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Onion Alpha: {config.onion_alpha || 1.0}</Label>
                        <Slider
                          value={[config.onion_alpha || 1.0]}
                          onValueChange={([v]) => {
                            setConfig({ ...config, onion_alpha: v });
                            setConfigDirty(true);
                          }}
                          min={0}
                          max={2}
                          step={0.1}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Weight for unweighted degree (# of connections)
                        </p>
                      </div>
                      <div>
                        <Label>Onion Beta: {config.onion_beta || 1.0}</Label>
                        <Slider
                          value={[config.onion_beta || 1.0]}
                          onValueChange={([v]) => {
                            setConfig({ ...config, onion_beta: v });
                            setConfigDirty(true);
                          }}
                          min={0}
                          max={2}
                          step={0.1}
                          className="mt-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Weight for weighted degree (sum of edge weights)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Holdings Lookback */}
                <div className="space-y-4">
                  <h3 className="font-medium">Holdings Window</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Holdings Lookback (months): {config.holdings_lookback_months || 5}</Label>
                      <Slider
                        value={[config.holdings_lookback_months || 5]}
                        onValueChange={([v]) => {
                          setConfig({ ...config, holdings_lookback_months: v });
                          setConfigDirty(true);
                        }}
                        min={1}
                        max={12}
                        step={1}
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label>Skill Tolerance (days): {config.asof_tolerance_days || 183}</Label>
                      <Slider
                        value={[config.asof_tolerance_days || 183]}
                        onValueChange={([v]) => {
                          setConfig({ ...config, asof_tolerance_days: v });
                          setConfigDirty(true);
                        }}
                        min={30}
                        max={365}
                        step={1}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Feature Toggles */}
                <div className="space-y-4">
                  <h3 className="font-medium">Feature Toggles</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center justify-between">
                      <Label>Process Global Graphs</Label>
                      <Switch
                        checked={config.process_global_graphs !== false}
                        onCheckedChange={(checked) => {
                          setConfig({ ...config, process_global_graphs: checked });
                          setConfigDirty(true);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Process Sector Graphs</Label>
                      <Switch
                        checked={config.process_sector_graphs !== false}
                        onCheckedChange={(checked) => {
                          setConfig({ ...config, process_sector_graphs: checked });
                          setConfigDirty(true);
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Test Mode</Label>
                      <Switch
                        checked={config.test_mode === true}
                        onCheckedChange={(checked) => {
                          setConfig({ ...config, test_mode: checked });
                          setConfigDirty(true);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {configDirty && (
                  <div className="flex justify-end">
                    <Button onClick={handleSaveConfig}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Configuration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Pipeline Logs</CardTitle>
                  <select
                    value={selectedRunId}
                    onChange={(e) => setSelectedRunId(e.target.value)}
                    className="p-2 rounded border bg-background text-sm"
                  >
                    {runHistory.map(run => (
                      <option key={run.run_id} value={run.run_id}>
                        {run.run_id} - {run.run_type} ({run.status})
                      </option>
                    ))}
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] font-mono text-xs">
                  {runLogs.map((log, i) => (
                    <div 
                      key={i} 
                      className={`py-1 px-2 border-b border-muted/30 ${
                        log.level === 'error' ? 'bg-red-500/10 text-red-400' :
                        log.level === 'warning' ? 'bg-amber-500/10 text-amber-400' :
                        ''
                      }`}
                    >
                      <span className="text-muted-foreground">
                        {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : '--:--:--'}
                      </span>
                      {' '}
                      <span className={
                        log.level === 'error' ? 'text-red-400' :
                        log.level === 'warning' ? 'text-amber-400' :
                        'text-blue-400'
                      }>
                        [{log.level?.toUpperCase()}]
                      </span>
                      {' '}
                      <span>{log.message}</span>
                    </div>
                  ))}
                  {runLogs.length === 0 && (
                    <div className="py-8 text-center text-muted-foreground">
                      No logs available. Select a run or start a new pipeline.
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Run History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-background">
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Run ID</th>
                        <th className="text-left py-2 px-2">Type</th>
                        <th className="text-left py-2 px-2">Status</th>
                        <th className="text-left py-2 px-2">Started</th>
                        <th className="text-right py-2 px-2">Duration</th>
                        <th className="text-right py-2 px-2">Dates</th>
                        <th className="text-right py-2 px-2">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runHistory.map(run => (
                        <tr 
                          key={run.run_id} 
                          className="border-b border-muted/50 hover:bg-muted/20 cursor-pointer"
                          onClick={() => {
                            setSelectedRunId(run.run_id);
                            setActiveTab("logs");
                          }}
                        >
                          <td className="py-2 px-2 font-mono">{run.run_id}</td>
                          <td className="py-2 px-2">{run.run_type}</td>
                          <td className="py-2 px-2">
                            <Badge variant={
                              run.status === 'completed' ? 'default' :
                              run.status === 'running' ? 'secondary' :
                              run.status === 'failed' ? 'destructive' :
                              'outline'
                            }>
                              {run.status}
                            </Badge>
                          </td>
                          <td className="py-2 px-2">
                            {run.started_at ? new Date(run.started_at).toLocaleString() : '-'}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {run.duration_seconds ? `${run.duration_seconds}s` : '-'}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {run.observation_dates_processed ?? '-'}
                          </td>
                          <td className="py-2 px-2 text-right">
                            {run.rows_written?.toLocaleString() ?? '-'}
                          </td>
                        </tr>
                      ))}
                      {runHistory.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-8 text-center text-muted-foreground">
                            No runs yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
};

export default CentralityPage;
